import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import './CampusGame.css';

interface CampusGameProps {
  onNavigate: (page: string) => void;
  showToast?: (message: string) => void;
  onPointsEarned?: (points: number, type?: string, location?: string) => void;
}

interface Bird {
  x: number;
  y: number;
  velocity: number;
  rotation: number;
}

interface Pipe {
  x: number;
  topHeight: number;
  bottomY: number;
  width: number;
  passed: boolean;
}

const CampusGame: React.FC<CampusGameProps> = ({ onNavigate, showToast, onPointsEarned }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  
  // PERFORMANCE FIX: Remove React state from game loop - use only refs
  const gameStateRef = useRef({
    bird: { x: 80, y: 250, velocity: 0, rotation: 0 } as Bird,
    pipes: [] as Pipe[],
    score: 0,
    gameOver: false,
    started: false,
    frameCount: 0,
    groundX: 0
  });

  const { user, updatePoints } = useAuth();
  
  // PERFORMANCE FIX: Throttle React state updates
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const [highScore, setHighScore] = useState(() => 
    parseInt(localStorage.getItem('campusGameHighScore') || '0')
  );
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState<Array<{name: string, score: number, department?: string}>>([]);
  const [leaderboardScroll, setLeaderboardScroll] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [playerRank, setPlayerRank] = useState<number | null>(null);

  // Images and Audio - KEEP ALL ORIGINAL SPRITES
  const birdImagesRef = useRef<HTMLImageElement[]>([]);
  const pipeImageRef = useRef<HTMLImageElement | null>(null);
  const backgroundImageRef = useRef<HTMLImageElement | null>(null);
  const groundImageRef = useRef<HTMLImageElement | null>(null);
  const gameoverImageRef = useRef<HTMLImageElement | null>(null);
  const messageImageRef = useRef<HTMLImageElement | null>(null);
  const leaderboardImageRef = useRef<HTMLImageElement | null>(null);
  const playButtonImageRef = useRef<HTMLImageElement | null>(null);
  const scoreboardImageRef = useRef<HTMLImageElement | null>(null);
  const numberImagesRef = useRef<HTMLImageElement[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  
  // Audio - skip on iOS (causes lag), enabled elsewhere
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  
  // Constants - DELTA-TIME BASED PHYSICS for consistent feel across all frame rates
  const isMobile = window.innerWidth < 768;
  // FULLSCREEN on mobile - use most of the screen
  const CANVAS_WIDTH = isMobile ? Math.min(window.innerWidth - 20, 400) : 400;
  const CANVAS_HEIGHT = isMobile ? Math.min(window.innerHeight - 140, 700) : 600;
  const BIRD_WIDTH = 34;
  const BIRD_HEIGHT = 24;
  
  // Physics tuned for smooth, playable feel (all values in pixels per second)
  const GRAVITY = 1400; // acceleration: how fast bird speeds up when falling
  const JUMP_VELOCITY = -420; // initial upward velocity on tap (stronger jump)
  const TERMINAL_VELOCITY = 500; // max fall speed - prevents "rocket" feeling
  
  const PIPE_WIDTH = 52;
  const PIPE_GAP_MIN = 120; // Random gap between 120-160px for variety
  const PIPE_GAP_MAX = 160;
  const PIPE_SPEED = 145; // pixels per second (faster pace)
  const GROUND_HEIGHT = 112;
  const PIPE_SPACING_START = 200; // Starting distance between pipes
  const PIPE_SPACING_MIN = 150; // Minimum distance (gets harder)

  // Load audio on mount (skip iOS)
  useEffect(() => {
    if (isIOS) return;
    const sounds = ['wing', 'hit', 'point'];
    sounds.forEach(name => {
      const audio = new Audio(`/sounds/${name}.wav`);
      audio.volume = 0.3;
      audioElementsRef.current.set(name, audio);
    });
  }, [isIOS]);

  // Load images and audio - KEEP ALL ORIGINAL SPRITES
  useEffect(() => {
    let loadedCount = 0;
    const totalImages = 21; // All original sprites

    const onImageLoad = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        setImagesLoaded(true);
      }
    };

    // Audio will be initialized on first user tap (required for mobile)

    // Bird sprites - ORIGINAL
    for (let i = 0; i < 3; i++) {
      const img = new Image();
      img.onload = onImageLoad;
      img.onerror = onImageLoad;
      const frames = ['upflap', 'midflap', 'downflap'];
      img.src = `/game-sprites/yellowbird-${frames[i]}.png`;
      birdImagesRef.current[i] = img;
    }

    // Other sprites - ALL ORIGINAL
    const pipeImg = new Image();
    pipeImg.onload = onImageLoad;
    pipeImg.onerror = onImageLoad;
    pipeImg.src = '/game-sprites/pipe-green.png';
    pipeImageRef.current = pipeImg;

    const bgImg = new Image();
    bgImg.onload = onImageLoad;
    bgImg.onerror = onImageLoad;
    bgImg.src = '/game-sprites/background-day.png';
    backgroundImageRef.current = bgImg;

    const groundImg = new Image();
    groundImg.onload = onImageLoad;
    groundImg.onerror = onImageLoad;
    groundImg.src = '/game-sprites/base.png';
    groundImageRef.current = groundImg;

    const gameoverImg = new Image();
    gameoverImg.onload = onImageLoad;
    gameoverImg.onerror = onImageLoad;
    gameoverImg.src = '/game-sprites/gameover.png';
    gameoverImageRef.current = gameoverImg;

    const messageImg = new Image();
    messageImg.onload = onImageLoad;
    messageImg.onerror = onImageLoad;
    messageImg.src = '/game-sprites/message.png';
    messageImageRef.current = messageImg;

    const leaderboardImg = new Image();
    leaderboardImg.onload = onImageLoad;
    leaderboardImg.onerror = onImageLoad;
    leaderboardImg.src = '/game-sprites/leader.png';
    leaderboardImageRef.current = leaderboardImg;

    const playButtonImg = new Image();
    playButtonImg.onload = onImageLoad;
    playButtonImg.onerror = onImageLoad;
    playButtonImg.src = '/playy.png';
    playButtonImageRef.current = playButtonImg;

    const scoreboardImg = new Image();
    scoreboardImg.onload = onImageLoad;
    scoreboardImg.onerror = onImageLoad;
    scoreboardImg.src = '/leaderboard-btn.png';
    scoreboardImageRef.current = scoreboardImg;

    // Number sprites (0-9) - ORIGINAL
    for (let i = 0; i < 10; i++) {
      const numImg = new Image();
      numImg.onload = onImageLoad;
      numImg.onerror = onImageLoad;
      numImg.src = `/game-sprites/${i}.png`;
      numberImagesRef.current[i] = numImg;
    }
  }, []);

  const resetGame = useCallback(() => {
    gameStateRef.current = {
      bird: { x: 80, y: 300, velocity: 0, rotation: 0 },
      pipes: [],
      score: 0,
      gameOver: false,
      started: false,
      frameCount: 0,
      groundX: 0
    };
    // Reset frame timing
    lastFrameTimeRef.current = performance.now();
    // Reset cached context in case canvas changed
    ctxRef.current = null;
    
    setScore(0);
    setGameOver(false);
    setStarted(false);
    setShowLeaderboard(false);
  }, []);

  // KEEP ALL ORIGINAL LEADERBOARD FUNCTIONS
  const submitScore = useCallback(async (score: number) => {
    if (!user || score <= 0) return;

    try {
      // First check if user already has a higher score
      const { data: existing } = await supabase
        .from('game_scores')
        .select('score')
        .eq('user_id', user.id)
        .single();
      
      // Only update if new score is higher (or no existing score)
      if (existing && existing.score >= score) {
        // Existing score is higher, don't overwrite
        await loadLeaderboard();
        return;
      }
      
      const { error } = await supabase
        .from('game_scores')
        .upsert({
          user_id: user.id,
          user_name: user.name,
          department: user.department || 'Unknown',
          score: score,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id',
          ignoreDuplicates: false
        });

      if (error) throw error;
      
      await loadLeaderboard();
    } catch (error) {
      console.error('Error submitting score:', error);
    }
  }, [user]);

  const loadLeaderboard = useCallback(async () => {
    try {
      const { data: scores, error } = await supabase
        .from('game_scores')
        .select('user_name, score, department')
        .order('score', { ascending: false })
        .limit(1000);

      if (error) throw error;

      if (scores) {
        setLeaderboard(scores.map(s => ({
          name: s.user_name,
          score: s.score,
          department: s.department
        })));
      }

      const { count, error: countError } = await supabase
        .from('game_scores')
        .select('*', { count: 'exact', head: true });

      if (!countError && count) {
        setTotalPlayers(count);
      }

      if (user) {
        const { data: rankData, error: rankError } = await supabase
          .rpc('get_player_rank', { player_user_id: user.id });

        if (!rankError && rankData) {
          setPlayerRank(rankData);
        }
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadLeaderboard();
    }
  }, [user, loadLeaderboard]);

  useEffect(() => {
    if (showLeaderboard && user && leaderboard.length > 0) {
      const playerIndex = leaderboard.findIndex(entry => entry.name === user.name);
      if (playerIndex >= 0) {
        const entriesPerPage = isMobile ? 6 : 12;
        const targetScroll = Math.max(0, Math.min(playerIndex - Math.floor(entriesPerPage / 2), leaderboard.length - entriesPerPage));
        setLeaderboardScroll(targetScroll);
      }
    }
  }, [showLeaderboard, user, leaderboard, isMobile]);

  const handleLeaderboardScroll = useCallback((direction: 'up' | 'down') => {
    const entriesPerPage = isMobile ? 6 : 12; // Match the display
    const maxScroll = Math.max(0, leaderboard.length - entriesPerPage);
    
    if (direction === 'up') {
      setLeaderboardScroll(Math.max(0, leaderboardScroll - 1));
    } else {
      setLeaderboardScroll(Math.min(maxScroll, leaderboardScroll + 1));
    }
  }, [leaderboardScroll, leaderboard.length, isMobile]);

  // KEEP ORIGINAL DRAW FUNCTIONS
  const drawPixelText = useCallback((ctx: CanvasRenderingContext2D, text: string, x: number, y: number, scale = 1) => {
    ctx.fillStyle = '#FFF';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.font = `${12 * scale}px monospace`;
    ctx.textAlign = 'left';
    
    ctx.strokeText(text, x, y);
    ctx.fillText(text, x, y);
  }, []);

  const drawSpriteNumber = useCallback((ctx: CanvasRenderingContext2D, number: number, x: number, y: number, scale = 1, align = 'left') => {
    const numberStr = number.toString();
    const digitWidth = 24 * scale;
    const digitHeight = 36 * scale;
    
    let totalWidth = numberStr.length * digitWidth;
    let startX = x;
    
    if (align === 'center') {
      startX = x - totalWidth / 2;
    } else if (align === 'right') {
      startX = x - totalWidth;
    }
    
    for (let i = 0; i < numberStr.length; i++) {
      const digit = parseInt(numberStr[i]);
      const digitImg = numberImagesRef.current[digit];
      
      if (digitImg && digitImg.complete) {
        ctx.drawImage(
          digitImg, 
          startX + (i * digitWidth), 
          y, 
          digitWidth, 
          digitHeight
        );
      }
    }
    
    return totalWidth;
  }, []);

  // Play sound (skip iOS)
  const playSound = useCallback((soundName: string) => {
    if (isIOS) return;
    const audio = audioElementsRef.current.get(soundName);
    if (audio) {
      audio.currentTime = 0;
      audio.play().catch(() => {});
    }
  }, [isIOS]);

  const jump = useCallback(() => {
    if (!imagesLoaded) return;

    playSound('wing');

    if (!gameStateRef.current.started) {
      gameStateRef.current.started = true;
      gameStateRef.current.bird.velocity = JUMP_VELOCITY;
      setStarted(true);
    } else if (gameStateRef.current.gameOver) {
      resetGame();
    } else {
      gameStateRef.current.bird.velocity = JUMP_VELOCITY;
    }
  }, [imagesLoaded, resetGame, playSound]);

  const checkCollisions = useCallback((bird: Bird, pipes: Pipe[]) => {
    // Ground collision
    if (bird.y + BIRD_HEIGHT / 2 >= CANVAS_HEIGHT - GROUND_HEIGHT) {
      return true;
    }
    
    // Ceiling collision
    if (bird.y - BIRD_HEIGHT / 2 <= 0) {
      return true;
    }

    // Pipe collision - ORIGINAL LOGIC
    for (const pipe of pipes) {
      if (
        bird.x + BIRD_WIDTH / 2 > pipe.x &&
        bird.x - BIRD_WIDTH / 2 < pipe.x + pipe.width
      ) {
        if (bird.y - BIRD_HEIGHT / 2 < pipe.topHeight || 
            bird.y + BIRD_HEIGHT / 2 > pipe.bottomY) {
          return true;
        }
      }
    }

    return false;
  }, []);

  // KEEP ALL ORIGINAL DRAWING CODE - EXACT SAME SPRITES AND RENDERING
  const draw = useCallback((ctx: CanvasRenderingContext2D) => {
    const state = gameStateRef.current;
    
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    if (!imagesLoaded) {
      ctx.fillStyle = '#70c5ce';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.fillStyle = '#FFF';
      ctx.font = 'bold 20px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Loading...', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
      return;
    }

    // Background - ORIGINAL
    if (backgroundImageRef.current && backgroundImageRef.current.complete) {
      const scaleX = CANVAS_WIDTH / backgroundImageRef.current.width;
      const scaleY = CANVAS_HEIGHT / backgroundImageRef.current.height;
      const scale = Math.max(scaleX, scaleY);
      
      const scaledWidth = backgroundImageRef.current.width * scale;
      const scaledHeight = backgroundImageRef.current.height * scale;
      
      const offsetX = (CANVAS_WIDTH - scaledWidth) / 2;
      const offsetY = (CANVAS_HEIGHT - scaledHeight) / 2;
      
      ctx.drawImage(backgroundImageRef.current, offsetX, offsetY, scaledWidth, scaledHeight);
    } else {
      ctx.fillStyle = '#70c5ce';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }

    // Pipes - ORIGINAL SPRITES
    if (pipeImageRef.current && pipeImageRef.current.complete) {
      state.pipes.forEach(pipe => {
        ctx.save();
        ctx.scale(1, -1);
        ctx.drawImage(pipeImageRef.current!, pipe.x, -pipe.topHeight, pipe.width, pipe.topHeight);
        ctx.restore();

        ctx.drawImage(pipeImageRef.current!, pipe.x, pipe.bottomY, pipe.width, CANVAS_HEIGHT - GROUND_HEIGHT - pipe.bottomY);
      });
    } else {
      state.pipes.forEach(pipe => {
        ctx.fillStyle = '#228B22';
        ctx.fillRect(pipe.x, 0, pipe.width, pipe.topHeight);
        ctx.fillRect(pipe.x, pipe.bottomY, pipe.width, CANVAS_HEIGHT - GROUND_HEIGHT - pipe.bottomY);
        
        ctx.strokeStyle = '#1a5f1a';
        ctx.lineWidth = 2;
        ctx.strokeRect(pipe.x, 0, pipe.width, pipe.topHeight);
        ctx.strokeRect(pipe.x, pipe.bottomY, pipe.width, CANVAS_HEIGHT - GROUND_HEIGHT - pipe.bottomY);
      });
    }

    // Ground - ORIGINAL
    const groundY = CANVAS_HEIGHT - GROUND_HEIGHT;
    if (groundImageRef.current && groundImageRef.current.complete) {
      const groundWidth = 336;
      for (let x = state.groundX; x < CANVAS_WIDTH + groundWidth; x += groundWidth) {
        ctx.drawImage(groundImageRef.current, x, groundY);
      }
    } else {
      ctx.fillStyle = '#DED895';
      ctx.fillRect(0, groundY, CANVAS_WIDTH, GROUND_HEIGHT);
    }

    // Bird - ORIGINAL SPRITES AND ANIMATION
    if (birdImagesRef.current.length === 3 && birdImagesRef.current[0].complete) {
      const frameIndex = Math.floor(state.frameCount / 8) % 3;
      const birdImage = birdImagesRef.current[frameIndex];
      
      if (birdImage && birdImage.complete) {
        ctx.save();
        ctx.translate(state.bird.x, state.bird.y);
        ctx.rotate((state.bird.rotation * Math.PI) / 180);
        ctx.drawImage(birdImage, -BIRD_WIDTH / 2, -BIRD_HEIGHT / 2, BIRD_WIDTH, BIRD_HEIGHT);
        ctx.restore();
      }
    } else {
      ctx.fillStyle = state.gameOver ? '#FF6B6B' : '#FFD700';
      ctx.beginPath();
      ctx.arc(state.bird.x, state.bird.y, BIRD_WIDTH / 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Score - ORIGINAL SPRITE NUMBERS
    if (numberImagesRef.current.length > 0 && numberImagesRef.current[0]?.complete) {
      drawSpriteNumber(ctx, state.score, CANVAS_WIDTH / 2, 30, 1.2, 'center');
    } else {
      ctx.fillStyle = '#FFF';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.strokeText(`${state.score}`, CANVAS_WIDTH / 2, 60);
      ctx.fillText(`${state.score}`, CANVAS_WIDTH / 2, 60);
    }

    // ALL ORIGINAL GAME SCREENS - Start, Game Over, Leaderboard - KEEPING EVERYTHING EXACTLY THE SAME
    if (!state.started) {
      const pulseScale = 1 + Math.sin(state.frameCount * 0.025) * 0.1;
      const alpha = 0.8 + Math.sin(state.frameCount * 0.03) * 0.2;
      
      ctx.save();
      ctx.translate(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 100);
      ctx.scale(pulseScale, pulseScale);
      
      ctx.fillStyle = `rgba(0, 0, 0, ${alpha * 0.5})`;
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Tap to Start!', 2, 2);
      
      ctx.fillStyle = '#FFF';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      
      ctx.strokeText('Tap to Start!', 0, 0);
      ctx.fillText('Tap to Start!', 0, 0);
      
      ctx.restore();
    }

    // Game over screen - ORIGINAL with ALL sprites
    if (state.gameOver && !showLeaderboard) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      
      if (gameoverImageRef.current && gameoverImageRef.current.complete) {
        const gameoverImg = gameoverImageRef.current;
        const scale = 1.5;
        const gameoverW = gameoverImg.width * scale;
        const gameoverH = gameoverImg.height * scale;
        const gameoverX = CANVAS_WIDTH / 2 - gameoverW / 2;
        const gameoverY = 60;
        
        ctx.drawImage(gameoverImg, gameoverX, gameoverY, gameoverW, gameoverH);
      }
      
      if (leaderboardImageRef.current && leaderboardImageRef.current.complete) {
        const leaderImg = leaderboardImageRef.current;
        const scale = 0.2;
        const leaderW = leaderImg.width * scale;
        const leaderH = leaderImg.height * scale;
        const leaderX = CANVAS_WIDTH / 2 - leaderW / 2;
        const leaderY = 180;
        
        ctx.drawImage(leaderImg, leaderX, leaderY, leaderW, leaderH);
        
        const scoreX = leaderX + leaderW * 0.81;
        const currentScoreY = leaderY + leaderH * 0.3;
        const bestScoreY = leaderY + leaderH * 0.65;
        
        if (numberImagesRef.current.length > 0 && numberImagesRef.current[0]?.complete) {
          drawSpriteNumber(ctx, state.score, scoreX, currentScoreY, 0.6, 'center');
          drawSpriteNumber(ctx, highScore, scoreX, bestScoreY, 0.7, 'center');
        } else {
          ctx.fillStyle = '#8B4513';
          ctx.font = 'bold 35px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(state.score.toString(), scoreX, currentScoreY);
          ctx.fillText(highScore.toString(), scoreX, bestScoreY);
        }
      }
      
      // Buttons - ORIGINAL sprites
      let buttonY;
      if (leaderboardImageRef.current && leaderboardImageRef.current.complete) {
        const scale = 0.15;
        const leaderH = leaderboardImageRef.current.height * scale;
        const leaderY = 180;
        buttonY = leaderY + leaderH + 20;
      } else {
        buttonY = 320;
      }
      
      const buttonW = 80;
      const buttonH = 35;
      const playButtonX = CANVAS_WIDTH / 2 - 85;
      const scoreButtonX = CANVAS_WIDTH / 2 + 5;
      
      // Play button - draw sprite or fallback
      if (playButtonImageRef.current && playButtonImageRef.current.complete && playButtonImageRef.current.naturalWidth > 0) {
        // Scale to fit button size while maintaining aspect ratio
        const imgW = playButtonImageRef.current.naturalWidth;
        const imgH = playButtonImageRef.current.naturalHeight;
        const scale = Math.min(buttonW / imgW, buttonH / imgH) * 0.9;
        const btnW = imgW * scale;
        const btnH = imgH * scale;
        const btnX = playButtonX + (buttonW - btnW) / 2;
        const btnY = buttonY + (buttonH - btnH) / 2;
        ctx.drawImage(playButtonImageRef.current, btnX, btnY, btnW, btnH);
      } else {
        // Fallback: draw play button
        ctx.fillStyle = '#4CAF50';
        ctx.fillRect(playButtonX, buttonY, buttonW, buttonH);
        ctx.strokeStyle = '#388E3C';
        ctx.lineWidth = 2;
        ctx.strokeRect(playButtonX, buttonY, buttonW, buttonH);
        
        ctx.fillStyle = '#FFF';
        ctx.font = 'bold 20px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('▶', playButtonX + buttonW/2, buttonY + buttonH/2 + 7);
      }
      
      // Leaderboard button - draw sprite or fallback
      if (scoreboardImageRef.current && scoreboardImageRef.current.complete && scoreboardImageRef.current.naturalWidth > 0) {
        const imgW = scoreboardImageRef.current.naturalWidth;
        const imgH = scoreboardImageRef.current.naturalHeight;
        const scale = Math.min(buttonW / imgW, buttonH / imgH) * 0.9;
        const btnW = imgW * scale;
        const btnH = imgH * scale;
        const btnX = scoreButtonX + (buttonW - btnW) / 2;
        const btnY = buttonY + (buttonH - btnH) / 2;
        ctx.drawImage(scoreboardImageRef.current, btnX, btnY, btnW, btnH);
      } else {
        // Fallback: draw leaderboard button
        ctx.fillStyle = '#2196F3';
        ctx.fillRect(scoreButtonX, buttonY, buttonW, buttonH);
        ctx.strokeStyle = '#1976D2';
        ctx.lineWidth = 2;
        ctx.strokeRect(scoreButtonX, buttonY, buttonW, buttonH);
        
        ctx.fillStyle = '#FFF';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('🏆', scoreButtonX + buttonW/2, buttonY + buttonH/2 + 6);
      }
    }
    
    // MOBILE-OPTIMIZED LEADERBOARD DISPLAY
    if (showLeaderboard) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      
      // Scale everything based on canvas size
      const scale = CANVAS_WIDTH / 400; // 1.0 on desktop, 0.75 on mobile
      const panelMargin = isMobile ? 15 : 50;
      const panelX = panelMargin;
      const panelY = isMobile ? 60 : 100;
      const panelW = CANVAS_WIDTH - (panelMargin * 2);
      const panelH = isMobile ? 300 : 350;
      
      ctx.fillStyle = 'rgba(30, 30, 30, 0.95)';
      ctx.fillRect(panelX, panelY, panelW, panelH);
      ctx.strokeStyle = '#FFF';
      ctx.lineWidth = 2;
      ctx.strokeRect(panelX, panelY, panelW, panelH);
      
      // Title
      ctx.fillStyle = '#FFD700';
      ctx.font = `bold ${isMobile ? 20 : 28}px Arial`;
      ctx.textAlign = 'center';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.strokeText('LEADERBOARD', CANVAS_WIDTH / 2, panelY + (isMobile ? 28 : 40));
      ctx.fillText('LEADERBOARD', CANVAS_WIDTH / 2, panelY + (isMobile ? 28 : 40));
      
      // Close button
      const closeBtnSize = isMobile ? 24 : 30;
      const closeX = panelX + panelW - (closeBtnSize / 2) - 8;
      const closeY = panelY + (closeBtnSize / 2) + 8;
      ctx.fillStyle = '#E74C3C';
      ctx.fillRect(closeX - closeBtnSize/2, closeY - closeBtnSize/2, closeBtnSize, closeBtnSize);
      ctx.strokeStyle = '#C0392B';
      ctx.lineWidth = 2;
      ctx.strokeRect(closeX - closeBtnSize/2, closeY - closeBtnSize/2, closeBtnSize, closeBtnSize);
      
      ctx.fillStyle = '#FFF';
      ctx.font = `bold ${isMobile ? 16 : 20}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('✕', closeX, closeY + (isMobile ? 5 : 7));
      
      // Entries configuration - fewer on mobile, taller rows for 2-line layout
      const entriesPerPage = isMobile ? 6 : 12;
      const entryHeight = isMobile ? 36 : 25; // Taller on mobile for name + dept
      const startIndex = Math.floor(leaderboardScroll);
      const maxScroll = Math.max(0, leaderboard.length - entriesPerPage);
      const listStartY = panelY + (isMobile ? 50 : 70);
      
      // Scrollbar
      if (leaderboard.length > entriesPerPage) {
        const scrollBarX = panelX + panelW - 12;
        const scrollBarY = listStartY;
        const scrollBarH = entryHeight * entriesPerPage;
        const scrollThumbH = Math.max(20, (entriesPerPage / leaderboard.length) * scrollBarH);
        const scrollThumbY = scrollBarY + (maxScroll > 0 ? (leaderboardScroll / maxScroll) * (scrollBarH - scrollThumbH) : 0);
        
        ctx.fillStyle = '#333';
        ctx.fillRect(scrollBarX, scrollBarY, 8, scrollBarH);
        
        ctx.fillStyle = '#666';
        ctx.fillRect(scrollBarX, scrollThumbY, 8, scrollThumbH);
      }
      
      if (leaderboard && leaderboard.length > 0) {
        const visibleEntries = leaderboard.slice(startIndex, startIndex + entriesPerPage);
        
        // Font sizes
        const rankFont = isMobile ? 'bold 11px monospace' : 'bold 12px monospace';
        const nameFont = isMobile ? 'bold 10px monospace' : 'bold 11px monospace';
        const deptFont = isMobile ? '8px monospace' : '9px monospace';
        const scoreFont = isMobile ? 'bold 10px monospace' : 'bold 11px monospace';
        
        // Column positions - responsive
        const rankX = panelX + 8;
        const nameX = panelX + (isMobile ? 32 : 50);
        const deptX = isMobile ? nameX : panelX + 160; // On mobile, dept goes under name
        const scoreX = panelX + panelW - (isMobile ? 20 : 40);
        
        visibleEntries.forEach((entry, index) => {
          const actualRank = startIndex + index + 1;
          const entryY = listStartY + 15 + (index * entryHeight);
          
          const isCurrentPlayer = user && entry.name === user.name;
          if (isCurrentPlayer) {
            ctx.fillStyle = 'rgba(0, 255, 0, 0.2)';
            ctx.fillRect(panelX + 3, entryY - 12, panelW - 20, entryHeight - 2);
          }
          
          // Rank with medal emoji for top 3
          const rankColor = actualRank === 1 ? '#FFD700' : actualRank === 2 ? '#C0C0C0' : actualRank === 3 ? '#CD7F32' : isCurrentPlayer ? '#00FF00' : '#FFF';
          ctx.fillStyle = rankColor;
          ctx.font = rankFont;
          ctx.textAlign = 'left';
          ctx.fillText(`${actualRank}.`, rankX, entryY);
          
          // Name
          ctx.fillStyle = isCurrentPlayer ? '#00FF00' : '#FFF';
          ctx.font = nameFont;
          const maxNameLen = isMobile ? 10 : 12;
          const displayName = entry.name.length > maxNameLen ? entry.name.substring(0, maxNameLen) + '..' : entry.name;
          ctx.fillText(displayName, nameX, isMobile ? entryY - 4 : entryY);
          
          // Department - on mobile show below name, on desktop show in column
          const dept = entry.department || 'N/A';
          const maxDeptLen = isMobile ? 12 : 10;
          const displayDept = dept.length > maxDeptLen ? dept.substring(0, maxDeptLen) + '..' : dept;
          ctx.fillStyle = isCurrentPlayer ? '#00AA00' : '#888';
          ctx.font = deptFont;
          if (isMobile) {
            // Show department below name on mobile
            ctx.fillText(displayDept, nameX, entryY + 8);
          } else {
            // Show in separate column on desktop
            ctx.fillText(displayDept, deptX, entryY);
          }
          
          // Score
          ctx.textAlign = 'right';
          ctx.fillStyle = isCurrentPlayer ? '#00FF00' : '#FFD700';
          ctx.font = scoreFont;
          ctx.fillText(entry.score.toString(), scoreX, entryY);
        });
        
        // Stats at bottom
        const statsY = panelY + panelH - (isMobile ? 20 : 35);
        ctx.fillStyle = '#AAA';
        ctx.font = `${isMobile ? 9 : 12}px monospace`;
        ctx.textAlign = 'center';
        ctx.fillText(`Players: ${totalPlayers}${playerRank ? ` | Rank: #${playerRank}` : ''}`, CANVAS_WIDTH / 2, statsY);
      } else {
        ctx.fillStyle = '#888';
        ctx.font = `italic ${isMobile ? 14 : 18}px Arial`;
        ctx.textAlign = 'center';
        ctx.fillText('Loading leaderboard...', CANVAS_WIDTH / 2, panelY + 150);
      }
      
      // Back button
      const btnW = isMobile ? 70 : 80;
      const btnH = isMobile ? 30 : 35;
      const backButtonX = CANVAS_WIDTH / 2 - btnW / 2;
      const backButtonY = panelY + panelH + (isMobile ? 10 : 20);
      ctx.fillStyle = '#E74C3C';
      ctx.fillRect(backButtonX, backButtonY, btnW, btnH);
      ctx.strokeStyle = '#C0392B';
      ctx.lineWidth = 2;
      ctx.strokeRect(backButtonX, backButtonY, btnW, btnH);
      
      ctx.fillStyle = '#FFF';
      ctx.font = `bold ${isMobile ? 14 : 16}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('Back', backButtonX + btnW/2, backButtonY + btnH/2 + 5);
    }
  }, [imagesLoaded, highScore, showLeaderboard, leaderboard, drawSpriteNumber, totalPlayers, playerRank, user, leaderboardScroll]);

  // PERFORMANCE FIX: Throttle React state updates
  let lastGameOverUpdate = 0;
  
  // Cache canvas context to avoid getting it every frame
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const lastFrameTimeRef = useRef(performance.now());
  
  const gameLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      animationRef.current = requestAnimationFrame(gameLoop);
      return;
    }
    
    // Cache context reference (with performance options)
    if (!ctxRef.current) {
      ctxRef.current = canvas.getContext('2d', { 
        alpha: false,  // Disable transparency for better performance
        desynchronized: true  // Reduce latency on supported browsers
      });
    }
    
    const ctx = ctxRef.current;
    if (!ctx) {
      animationRef.current = requestAnimationFrame(gameLoop);
      return;
    }

    const state = gameStateRef.current;
    
    // Calculate delta time for frame-independent physics
    const now = performance.now();
    const elapsed = now - lastFrameTimeRef.current;
    lastFrameTimeRef.current = now;
    const deltaTime = Math.min(elapsed / 1000, 0.05); // Cap at 50ms to prevent huge jumps
    
    // Always animate for preview effect
    state.frameCount++;

    // Ground scrolls only when not game over
    if (!state.gameOver) {
      const speedMultiplier = 1 + (Math.floor(state.score / 10) * 0.05); // Every 10 pipes = +5% speed
      state.groundX -= PIPE_SPEED * speedMultiplier * deltaTime;
      if (state.groundX <= -336) {
        state.groundX = 0;
      }
    }

    // Bird animation for start screen preview
    if (!state.started) {
      const floatOffset = Math.sin(state.frameCount * 0.05) * 8;
      state.bird.y = 300 + floatOffset;
      state.bird.rotation = Math.sin(state.frameCount * 0.08) * 5;
    }

    if (state.started && !state.gameOver) {
      // Bird physics - DELTA TIME BASED for consistent feel
      // Apply gravity (acceleration) to velocity
      state.bird.velocity += GRAVITY * deltaTime;
      
      // Clamp to terminal velocity (max fall speed)
      if (state.bird.velocity > TERMINAL_VELOCITY) {
        state.bird.velocity = TERMINAL_VELOCITY;
      }
      
      // Apply velocity to position
      state.bird.y += state.bird.velocity * deltaTime;
      
      // Clamp bird to not go below ground or above ceiling
      const groundLevel = CANVAS_HEIGHT - GROUND_HEIGHT - BIRD_HEIGHT / 2;
      if (state.bird.y > groundLevel) {
        state.bird.y = groundLevel;
      }
      if (state.bird.y < BIRD_HEIGHT / 2) {
        state.bird.y = BIRD_HEIGHT / 2;
      }
      
      // Bird rotation - based on velocity (in pixels/sec)
      if (state.bird.velocity < -100) {
        state.bird.rotation = -25; // Upward angle when rising
      } else if (state.bird.velocity > 100) {
        state.bird.rotation = Math.min(90, state.bird.velocity * 0.15); // Diving angle
      } else {
        state.bird.rotation = state.bird.velocity * 0.1; // Slight tilt
      }

      // Update pipes - DELTA TIME BASED
      state.pipes = state.pipes.filter(pipe => pipe.x + pipe.width > -10);
      
      for (const pipe of state.pipes) {
        // Increase speed based on score (every 10 pipes = +5% faster)
        const speedMultiplier = 1 + (Math.floor(state.score / 10) * 0.05);
        pipe.x -= PIPE_SPEED * speedMultiplier * deltaTime;
        
        if (!pipe.passed && pipe.x + pipe.width / 2 < state.bird.x) {
          pipe.passed = true;
          state.score++;
          setScore(state.score);
          playSound('point');
        }
      }

      // Generate new pipes with random gap sizes
      // Pipe spacing decreases as score increases (gets harder)
      const pipeSpacing = Math.max(PIPE_SPACING_MIN, PIPE_SPACING_START - (state.score * 2));
      if (state.pipes.length === 0 || state.pipes[state.pipes.length - 1].x < CANVAS_WIDTH - pipeSpacing) {
        // Random gap between min and max for variety
        const pipeGap = PIPE_GAP_MIN + Math.random() * (PIPE_GAP_MAX - PIPE_GAP_MIN);
        const minTopHeight = 60;
        const maxTopHeight = CANVAS_HEIGHT - GROUND_HEIGHT - pipeGap - 60;
        const topHeight = Math.random() * (maxTopHeight - minTopHeight) + minTopHeight;
        
        state.pipes.push({
          x: CANVAS_WIDTH,
          topHeight,
          bottomY: topHeight + pipeGap,
          width: PIPE_WIDTH,
          passed: false
        });
      }

      // Collision detection
      if (checkCollisions(state.bird, state.pipes)) {
        state.gameOver = true;
        
        const nowMs = performance.now();
        if (nowMs - lastGameOverUpdate > 100) {
          setGameOver(true);
          setScore(state.score);
          lastGameOverUpdate = nowMs;
        }
        
        playSound('hit');
        
        if (state.score > highScore) {
          setHighScore(state.score);
          localStorage.setItem('campusGameHighScore', state.score.toString());
        }
        
        if (user && state.score > 0) {
          submitScore(state.score);
        }

        if (state.score > highScore) {
          showToast?.(`🏆 New high score: ${state.score}!`);
        }
      }
    }

    draw(ctx);
    animationRef.current = requestAnimationFrame(gameLoop);
  }, [draw, checkCollisions, highScore, showToast, user, submitScore, playSound]);

  useEffect(() => {
    if (imagesLoaded) {
      animationRef.current = requestAnimationFrame(gameLoop);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameLoop, imagesLoaded]);

  // Input handlers
  useEffect(() => {
    const handleInteraction = (e: Event) => {
      e.preventDefault();
      
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const scaleX = CANVAS_WIDTH / rect.width;
      const scaleY = CANVAS_HEIGHT / rect.height;
      
      let clickX = 0, clickY = 0;
      if (e instanceof MouseEvent) {
        clickX = (e.clientX - rect.left) * scaleX;
        clickY = (e.clientY - rect.top) * scaleY;
      } else if (e instanceof TouchEvent && e.touches.length > 0) {
        clickX = (e.touches[0].clientX - rect.left) * scaleX;
        clickY = (e.touches[0].clientY - rect.top) * scaleY;
      }
      
      const state = gameStateRef.current;
      
      // Handle leaderboard screen clicks - MOBILE OPTIMIZED
      if (showLeaderboard) {
        const panelMargin = isMobile ? 15 : 50;
        const panelX = panelMargin;
        const panelY = isMobile ? 60 : 100;
        const panelW = CANVAS_WIDTH - (panelMargin * 2);
        const panelH = isMobile ? 300 : 350;
        
        // Close button hit area
        const closeBtnSize = isMobile ? 24 : 30;
        const closeX = panelX + panelW - (closeBtnSize / 2) - 8;
        const closeY = panelY + (closeBtnSize / 2) + 8;
        if (clickX >= closeX - closeBtnSize && clickX <= closeX + closeBtnSize && 
            clickY >= closeY - closeBtnSize && clickY <= closeY + closeBtnSize) {
          setShowLeaderboard(false);
          return;
        }
        
        // Scroll areas - top half scrolls up, bottom half scrolls down
        const listStartY = panelY + (isMobile ? 50 : 70);
        const listEndY = panelY + panelH - (isMobile ? 40 : 50);
        const listMidY = (listStartY + listEndY) / 2;
        
        if (clickX >= panelX && clickX <= panelX + panelW) {
          if (clickY >= listStartY && clickY <= listMidY) {
            handleLeaderboardScroll('up');
            return;
          } else if (clickY > listMidY && clickY <= listEndY) {
            handleLeaderboardScroll('down');
            return;
          }
        }

        // Back button
        const btnW = isMobile ? 70 : 80;
        const btnH = isMobile ? 30 : 35;
        const backButtonX = CANVAS_WIDTH / 2 - btnW / 2;
        const backButtonY = panelY + panelH + (isMobile ? 10 : 20);
        if (clickX >= backButtonX && clickX <= backButtonX + btnW && 
            clickY >= backButtonY && clickY <= backButtonY + btnH) {
          setShowLeaderboard(false);
          return;
        }
        return;
      }
      
      // Handle game over screen clicks - ORIGINAL
      if (state.gameOver) {
        let buttonY;
        if (leaderboardImageRef.current && leaderboardImageRef.current.complete) {
          const scale = 0.15;
          const leaderH = leaderboardImageRef.current.height * scale;
          const leaderY = 180;
          buttonY = leaderY + leaderH + 20;
        } else {
          buttonY = 320;
        }
        
        const buttonW = 80;
        const buttonH = 35;
        const playButtonX = CANVAS_WIDTH / 2 - 85;
        const scoreButtonX = CANVAS_WIDTH / 2 + 5;
        
        if (clickX >= playButtonX && clickX <= playButtonX + buttonW && 
            clickY >= buttonY && clickY <= buttonY + buttonH) {
          resetGame();
          return;
        }
        
        if (clickX >= scoreButtonX && clickX <= scoreButtonX + buttonW && 
            clickY >= buttonY && clickY <= buttonY + buttonH) {
          setShowLeaderboard(true);
          return;
        }
        return;
      }
      
      jump();
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('touchstart', handleInteraction, { passive: false });
      canvas.addEventListener('click', handleInteraction);
    }

    window.addEventListener('keydown', (e) => {
      if (e.code === 'Space' || e.key === 'ArrowUp') {
        e.preventDefault();
        jump();
      }
    });

    return () => {
      if (canvas) {
        canvas.removeEventListener('touchstart', handleInteraction);
        canvas.removeEventListener('click', handleInteraction);
      }
    };
  }, [jump, showLeaderboard, resetGame, handleLeaderboardScroll]);

  return (
    <div className="campus-game">
      <div className="campus-game-header">
        <button 
          className="campus-game-back-btn"
          onClick={() => onNavigate('dashboard')}
        >
          ← Back
        </button>
        <h2>Flappy Bird</h2>
        <div className="campus-game-user-info">
          <span>{user?.name}</span>
        </div>
      </div>

      <div className="campus-game-container">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="campus-game-canvas"
          style={{ 
            touchAction: 'none',
            userSelect: 'none',
            willChange: 'contents',
            transform: 'translateZ(0)'
          }}
        />
      </div>

      <div className="campus-game-stats">
        <div className="game-stat">
          <span className="stat-label">Score</span>
          <span className="stat-value">{score}</span>
        </div>
        <div className="game-stat">
          <span className="stat-label">Best</span>
          <span className="stat-value">{highScore}</span>
        </div>
      </div>
    </div>
  );
};

export default CampusGame;